import { CheckIcon } from "@/components/icons";
import { Button } from "@/components/ui/button";
import { Card } from "@/components/ui/card";
import { Input } from "@/components/ui/input";
import { Tabs, TabsList, TabsTrigger } from "@/components/ui/tabs";
import { Textarea } from "@/components/ui/textarea";
import { cn } from "cn";
import { useState } from "react";

import type { AgentPart } from "../agent-event.ts";

type QuestionsPart = Extract<AgentPart, { type: "question_request" }>;
type Item = QuestionsPart["questions"][number];

export interface GivenAnswer {
  readonly key: string;
  readonly selected: readonly string[];
  readonly custom?: string;
}

/**
 * Tarjeta de preguntas (sección 7.3.5).
 *
 * No es una confirmación: aquella aprueba o rechaza una acción ya preparada, y
 * esta recoge decisiones antes de preparar nada.
 *
 * Va por PESTAÑAS, una pregunta a la vez, y no todas apiladas. Apiladas, un
 * bloque de tres preguntas era un muro de doce opciones en el que no se veía
 * dónde empezaba una y acababa otra, y contestar la primera parecía haber
 * contestado el bloque entero. Con una delante y las demás como pestañas, en
 * todo momento se sabe cuántas van, cuáles faltan y que responder es UN gesto
 * al final -- el botón no dice "Responder" hasta que no queda ninguna.
 */
export function Questions({
  part,
  onAnswer,
  settled,
  disabled,
}: {
  part: QuestionsPart;
  onAnswer: (
    questionId: string,
    answers: readonly GivenAnswer[],
    note?: string,
  ) => void;
  /** Ya no espera a nadie: se respondió o caducó. */
  settled: boolean;
  disabled: boolean;
}) {
  const [picked, setPicked] = useState<Record<string, string[]>>({});
  /**
   * "Escribo la mía" va en su propio estado y no como una opción más con un
   * valor centinela: un centinela dentro de la lista de etiquetas puede chocar
   * con una opción de verdad, y las etiquetas las escribe el modelo.
   */
  const [writing, setWriting] = useState<Record<string, boolean>>({});
  const [custom, setCustom] = useState<Record<string, string>>({});
  const [note, setNote] = useState("");
  const [noting, setNoting] = useState(false);
  const [sentNow, setSentNow] = useState(false);
  const [active, setActive] = useState(part.questions[0]?.key ?? "");

  /**
   * Al releer la conversación, el backend dice qué tarjetas siguen esperando.
   * Una ya respondida se dibuja inerte: volver a ofrecerla llevaría a un
   * segundo envío que el agregado rechaza.
   */
  const sent = sentNow || settled;

  const answerFor = (item: Item): GivenAnswer | null => {
    if (writing[item.key] === true) {
      const written = (custom[item.key] ?? "").trim();
      return written === ""
        ? null
        : { key: item.key, selected: [], custom: written };
    }
    const chosen = picked[item.key] ?? [];
    return chosen.length === 0 ? null : { key: item.key, selected: chosen };
  };

  const given = part.questions
    .map(answerFor)
    .filter((answer) => answer !== null);
  const complete = given.length === part.questions.length;

  /** La siguiente sin responder, para no obligar a buscarla a mano. */
  const nextPending = (from: string): string | null =>
    part.questions
      .filter((item) => item.key !== from && answerFor(item) === null)
      .map((item) => item.key)[0] ?? null;

  const choose = (item: Item, label: string) => {
    setWriting((current) => ({ ...current, [item.key]: false }));
    setPicked((current) => {
      const now = current[item.key] ?? [];
      if (!item.multiSelect) return { ...current, [item.key]: [label] };
      return {
        ...current,
        [item.key]: now.includes(label)
          ? now.filter((l) => l !== label)
          : [...now, label],
      };
    });

    /**
     * De una sola respuesta se pasa sola a la siguiente que falte.
     *
     * Solo con `multiSelect` en false: donde se pueden marcar varias, saltar
     * al primer clic impediría marcar la segunda.
     */
    if (!item.multiSelect) {
      const next = nextPending(item.key);
      if (next !== null) setActive(next);
    }
  };

  const chooseOwn = (item: Item) => {
    setPicked((current) => ({ ...current, [item.key]: [] }));
    setWriting((current) => ({ ...current, [item.key]: true }));
  };

  const submit = () => {
    // Se bloquea al instante: sin esto, un doble clic manda dos peticiones.
    if (sent || !complete) return;
    setSentNow(true);
    onAnswer(part.questionId, given, note.trim() === "" ? undefined : note);
  };

  const current =
    part.questions.find((item) => item.key === active) ?? part.questions[0];
  const many = part.questions.length > 1;

  return (
    <Card
      className={cn(
        "border-l-primary gap-0 border-l-2 py-0",
        sent && "opacity-65",
      )}
    >
      <header className="flex flex-wrap items-center gap-x-2 gap-y-1 px-3 pt-2.5">
        <span className="text-primary text-xs font-medium">
          {sent ? "Respondido" : "Necesita tu respuesta"}
        </span>
        {many && !sent && (
          <span className="text-muted-foreground ml-auto text-xs tabular-nums">
            {given.length} de {part.questions.length}
          </span>
        )}
        {part.prompt !== null && (
          <p className="text-muted-foreground w-full text-xs">{part.prompt}</p>
        )}
      </header>

      {many && (
        <Tabs
          value={current?.key ?? ""}
          onValueChange={setActive}
          className="px-3 pt-2"
        >
          <TabsList
            variant="line"
            className="h-auto w-full flex-wrap justify-start gap-0.5"
          >
            {part.questions.map((item) => (
              <TabsTrigger
                key={item.key}
                value={item.key}
                disabled={sent}
                className="h-7 flex-none px-2 text-xs"
              >
                {answerFor(item) !== null && (
                  <CheckIcon className="text-success size-3.5" />
                )}
                {item.header}
              </TabsTrigger>
            ))}
          </TabsList>
        </Tabs>
      )}

      {current !== undefined && (
        <fieldset className="space-y-1.5 px-3 pt-2.5" disabled={sent}>
          <legend className="mb-1 text-sm font-medium">{current.text}</legend>

          {current.options.map((option) => (
            <Option
              key={option.label}
              label={option.label}
              description={option.description}
              active={
                writing[current.key] !== true &&
                (picked[current.key] ?? []).includes(option.label)
              }
              onSelect={() => {
                choose(current, option.label);
              }}
            />
          ))}

          <Option
            label="Otra"
            description="Ninguna encaja: la escribo yo."
            active={writing[current.key] === true}
            onSelect={() => {
              chooseOwn(current);
            }}
          />

          {writing[current.key] === true && (
            <Input
              autoFocus
              className="h-8 text-sm"
              placeholder="Tu respuesta"
              value={custom[current.key] ?? ""}
              onChange={(event) => {
                const { value } = event.target;
                setCustom((c) => ({ ...c, [current.key]: value }));
              }}
            />
          )}
        </fieldset>
      )}

      {noting && !sent && (
        <div className="px-3 pt-2">
          <Textarea
            autoFocus
            rows={2}
            className="text-sm"
            placeholder="Algo más que quieras añadir"
            value={note}
            onChange={(event) => {
              setNote(event.target.value);
            }}
          />
        </div>
      )}

      <footer className="flex items-center gap-2 px-3 py-2.5">
        {!sent && !noting && (
          <button
            type="button"
            className="text-muted-foreground hover:text-foreground text-xs"
            onClick={() => {
              setNoting(true);
            }}
          >
            Añadir nota
          </button>
        )}

        {settled && !sentNow && (
          <span className="text-muted-foreground text-xs">
            Estas preguntas ya se respondieron.
          </span>
        )}

        {!sent && (
          <div className="ml-auto flex items-center gap-2">
            <Next {...{ complete, current, nextPending, setActive }} />
            <Button
              size="sm"
              className="h-7"
              onClick={submit}
              disabled={disabled || !complete}
            >
              Responder
            </Button>
          </div>
        )}
      </footer>
    </Card>
  );
}

/**
 * El botón de pasar a la siguiente, cuando queda alguna.
 *
 * Existe para que "contesté una" y "contesté todas" no se parezcan: mientras
 * haya pendientes, el gesto que se ofrece es avanzar, y "Responder" sigue ahí
 * al lado pero apagado, diciendo sin palabras que aún no toca.
 */
function Next({
  complete,
  current,
  nextPending,
  setActive,
}: {
  complete: boolean;
  current: Item | undefined;
  nextPending: (from: string) => string | null;
  setActive: (key: string) => void;
}) {
  if (complete || current === undefined) return null;
  const next = nextPending(current.key);
  if (next === null) return null;

  return (
    <Button
      size="sm"
      variant="ghost"
      className="h-7"
      onClick={() => {
        setActive(next);
      }}
    >
      Siguiente
    </Button>
  );
}

/**
 * Una opción, con su descripción debajo.
 *
 * La descripción no es adorno: es lo que hace útil una pregunta con opciones.
 * Sin ella el usuario elige por el nombre y se entera después. Va en una línea
 * recortada y no en un párrafo: cuatro opciones con tres renglones cada una
 * son una pantalla entera para decidir una cosa.
 */
function Option({
  label,
  description,
  active,
  onSelect,
}: {
  label: string;
  description: string;
  active: boolean;
  onSelect: () => void;
}) {
  return (
    <button
      type="button"
      aria-pressed={active}
      onClick={onSelect}
      title={description}
      className={cn(
        "bg-card grid w-full gap-0.5 rounded-md border px-2.5 py-1.5 text-left transition-colors",
        "hover:border-muted-foreground/40 disabled:pointer-events-none disabled:opacity-60",
        active && "border-primary bg-primary/10",
      )}
    >
      <span className="text-sm leading-tight font-medium">{label}</span>
      <span className="text-muted-foreground truncate text-xs leading-tight">
        {description}
      </span>
    </button>
  );
}
